/**
 * CustomizeController — consent preferences modal.
 *
 * Reuses the theme's unified `.jkd-overlay` contract (backdrop, centered
 * panel, close button, scroll-lock). Necessary category is always checked +
 * disabled (locked). Closes on backdrop click, the close button, or Esc.
 */

// Category names and descriptions are plain-text site settings written into the
// panel as markup, so they are escaped before they reach innerHTML.
const ESCAPES = {'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'};
const esc = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ESCAPES[char]);

export default class CustomizeController {
	constructor({ root, store, copy }) {
		this.root = root;
		this.overlay = root.querySelector('[data-consent-popup]');
		this.inner = root.querySelector('[data-consent-popup-inner]');
		this.store = store;
		this.copy = copy;
		this._lastFocus = null;
		this._closeTimer = 0;
		this._onKeydown = this._onKeydown.bind(this);
	}

	init() {
		// The panel body is NOT built here. Most visitors accept or reject from
		// the banner and never open preferences, so parsing and inserting the
		// whole category list on every page load was work almost nobody used.
		// Wiring can still happen up front: both handlers are delegated on the
		// overlay and its inner container, which exist whether or not the body
		// has been rendered.
		this._wire();
	}

	open() {
		clearTimeout(this._closeTimer);
		this._lastFocus = document.activeElement;
		this.root.hidden = false;
		this.overlay.hidden = false;
		if (!this._rendered) {
			this._render();
			this._rendered = true;
		}
		this._syncToggles();
		// Force a frame before activating so the opacity/transform transition runs.
		requestAnimationFrame(() => this.overlay.classList.add('is-active'));
		document.documentElement.classList.add('jkd-overlay-locked');
		document.body.classList.add('jkd-overlay-locked');
		document.addEventListener('keydown', this._onKeydown);
		this.overlay.querySelector('.jkd-overlay__panel')?.focus();
	}

	close() {
		this.overlay.classList.remove('is-active');
		document.documentElement.classList.remove('jkd-overlay-locked');
		document.body.classList.remove('jkd-overlay-locked');
		document.removeEventListener('keydown', this._onKeydown);
		this._closeTimer = setTimeout(() => {
			this.overlay.hidden = true;
		}, 200); // match overlay transition duration
		if (this._lastFocus && typeof this._lastFocus.focus === 'function') {
			this._lastFocus.focus();
		}
	}

	_onKeydown(e) {
		if (e.key === 'Escape') this.close();
	}

	_render() {
		const categories = [
			{ id: 'necessary', label: this.copy.necessaryLabel, desc: this.copy.necessaryDesc, locked: true },
			{ id: 'analytics', label: this.copy.analyticsLabel, desc: this.copy.analyticsDesc },
			{ id: 'marketing', label: this.copy.marketingLabel, desc: this.copy.marketingDesc },
		];
		this.inner.innerHTML = `
			<h3 class="jkd-consent__popup-title h5">${esc(this.copy.customize)}</h3>
			<ul class="jkd-consent__cat-list">
				${categories.map((c) => `
					<li class="jkd-consent__cat" data-cat="${c.id}">
						<label>
							<input type="checkbox" data-cat-toggle ${c.locked ? 'checked disabled' : ''} />
							<span class="jkd-consent__cat-label">${esc(c.label)}</span>
						</label>
						<p class="jkd-consent__cat-desc">${esc(c.desc)}</p>
					</li>
				`).join('')}
			</ul>
			<div class="jkd-consent__popup-actions">
				<button type="button" class="jkd-btn sm" data-consent-popup-save>${esc(this.copy.save)}</button>
				<button type="button" class="jkd-btn sm outlined" data-consent-popup-reject>${esc(this.copy.reject)}</button>
			</div>
		`;
	}

	_syncToggles() {
		this.inner.querySelectorAll('[data-cat]').forEach((li) => {
			const cat = li.dataset.cat;
			if (cat === 'necessary') return;
			const input = li.querySelector('[data-cat-toggle]');
			input.checked = this.store.has(cat);
		});
	}

	_wire() {
		// Backdrop + close button (anywhere with [data-consent-popup-close]).
		this.overlay.addEventListener('click', (e) => {
			if (e.target.closest('[data-consent-popup-close]')) this.close();
		});

		this.inner.addEventListener('click', (e) => {
			if (e.target.closest('[data-consent-popup-save]')) {
				const granted = [];
				this.inner.querySelectorAll('[data-cat]').forEach((li) => {
					const cat = li.dataset.cat;
					if (cat === 'necessary') return;
					if (li.querySelector('[data-cat-toggle]').checked) granted.push(cat);
				});
				this.store.grant(granted);
				this.close();
			}
			if (e.target.closest('[data-consent-popup-reject]')) {
				this.store.grant([]); // necessary only
				this.close();
			}
		});
	}
}
